/* ==========================================================================
   VAN LYNN — product-gallery.js
   Scroll-snap gallery with thumbnails, variant media sync, video handling,
   and an accessible lightbox with click-to-zoom and pointer panning.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('product-gallery')) return;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.layout = this.dataset.layout || 'slider';
      if (!this.track || !this.slides.length) return;

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.goTo(thumb.dataset.mediaId))
      );
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

      this.track.addEventListener('scroll', window.VL.debounce(() => this.syncFromScroll(), 80), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      });

      const root = this.closest('[data-product-root]');
      root?.addEventListener('vl:variant-change', (e) => {
        const media = e.detail.variant && e.detail.variant.featured_media;
        if (media) this.goTo(String(media.id), true);
      });

      this.querySelectorAll('[data-gallery-zoom]').forEach((btn) =>
        btn.addEventListener('click', () => this.openLightbox(btn.dataset.mediaId, btn))
      );
      this.setActive(this.slides[0].dataset.mediaId);
    }

    isGridMode() {
      return this.layout !== 'slider' && window.matchMedia('(min-width: 990px)').matches;
    }

    behavior() {
      return document.documentElement.classList.contains('motion-ok') ? 'smooth' : 'auto';
    }

    indexOf(id) {
      return this.slides.findIndex((s) => s.dataset.mediaId === String(id));
    }

    step(dir) {
      const i = this.indexOf(this.activeId);
      const next = this.slides[(i + dir + this.slides.length) % this.slides.length];
      this.goTo(next.dataset.mediaId);
    }

    goTo(id, fromVariant = false) {
      const slide = this.slides[this.indexOf(id)];
      if (!slide) return;
      if (this.isGridMode()) {
        if (fromVariant && slide !== this.track.firstElementChild) {
          this.track.prepend(slide);
          this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        }
      } else {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: this.behavior() });
      }
      this.setActive(id);
    }

    syncFromScroll() {
      if (this.isGridMode()) return;
      const left = this.track.scrollLeft;
      let closest = this.slides[0];
      let min = Infinity;
      this.slides.forEach((s) => {
        const d = Math.abs(s.offsetLeft - this.track.offsetLeft - left);
        if (d < min) { min = d; closest = s; }
      });
      this.setActive(closest.dataset.mediaId);
    }

    setActive(id) {
      this.activeId = String(id);
      this.slides.forEach((s) => {
        const active = s.dataset.mediaId === this.activeId;
        s.classList.toggle('is-active', active);
        if (!active) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t) => {
        const active = t.dataset.mediaId === this.activeId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && t.parentElement) {
          const rail = t.closest('[data-thumb-rail]');
          if (rail) {
            const tr = t.getBoundingClientRect();
            const rr = rail.getBoundingClientRect();
            if (tr.left < rr.left || tr.right > rr.right || tr.top < rr.top || tr.bottom > rr.bottom) {
              t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.behavior() });
            }
          }
        }
      });
      if (this.counter) this.counter.textContent = `${this.indexOf(this.activeId) + 1} / ${this.slides.length}`;
    }

    /* ---------- Lightbox ---------- */
    openLightbox(id, trigger) {
      const lightbox = document.getElementById(this.dataset.lightbox);
      if (!lightbox) return;
      lightbox.open(trigger);
      const target = lightbox.querySelector(`[data-lightbox-item="${id}"]`);
      if (target) target.scrollIntoView({ block: 'start' });
      if (!lightbox.dataset.ready) {
        lightbox.dataset.ready = 'true';
        lightbox.querySelectorAll('[data-zoomable]').forEach((img) => this.bindZoom(img));
      }
    }

    bindZoom(wrapper) {
      const img = wrapper.querySelector('img');
      if (!img) return;
      wrapper.addEventListener('click', (e) => {
        const zoomed = wrapper.classList.toggle('is-zoomed');
        wrapper.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        if (zoomed) this.pan(e, wrapper, img);
        else img.style.transformOrigin = '';
      });
      wrapper.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); wrapper.click(); }
      });
      wrapper.addEventListener('pointermove', (e) => {
        if (wrapper.classList.contains('is-zoomed')) this.pan(e, wrapper, img);
      });
    }

    pan(e, wrapper, img) {
      const r = wrapper.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    }
  }

  customElements.define('product-gallery', ProductGallery);
})();
